/**
 * Asset Filtering Utilities
 *
 * This file contains utility functions for asset filtering, query parameter handling,
 * and filter-related operations.
 */

import type React from 'react';

import type { Filters } from '@/hooks/useAssets';

/**
 * Resets query parameters to clear problematic filters
 *
 * Useful when encountering query errors from invalid orderBy or filter parameters.
 * Clears sort, search, and advanced filters to return to a clean state.
 *
 * @param setFilters - Function to update filter state
 */
export const resetQueryParams = (
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
): void => {
  setFilters((prev: Filters) => ({
    ...prev,
    sort: undefined, // Clear sorting
    search: '', // Clear search
    advancedFilters: undefined, // Clear advanced filters
  }));
};

/**
 * Checks if an error is related to query parameters
 *
 * Analyzes error messages to determine if the error is caused by invalid
 * query parameters such as orderBy or filter fields.
 *
 * @param error - Error object with optional message property
 * @returns True if error is query-related
 */
export const isQueryError = (error: { message?: string } | null): boolean => {
  if (!error?.message) return false;

  return (
    error.message.includes('failed to unmarshal query') ||
    error.message.includes('orderBy') ||
    error.message.includes('invalid query')
  );
};
